import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, ShieldCheck, Workflow, CreditCard } from "lucide-react";
import Nav from "../site/Nav";
import Footer from "../site/Footer";
import IosInstallPrompt from "../site/IosInstallPrompt";
import { DIVISIONS, TOKEN_KEY, DIVISION_KEY, fmtMoney } from "../admin/adminUtils";
import { Stat } from "../admin/IntakeStat";
import IntakeRow from "../admin/IntakeRow";
import AdminTokenGate from "../admin/AdminTokenGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminIntakesPage() {
  const [token, setToken] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem(TOKEN_KEY) || "";
  });
  const [tokenInput, setTokenInput] = useState("");
  const [division, setDivision] = useState(() => {
    if (typeof window === "undefined") return "websites";
    const v = window.sessionStorage.getItem(DIVISION_KEY);
    return v === "revenue" ? "revenue" : "websites";
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const cfg = DIVISIONS[division];

  const load = async (bearer, div) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = DIVISIONS[div].endpoint;
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      setItems(res.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Token rejected. Check ADMIN_TOKEN and try again.");
        window.sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else if (status === 503) {
        setError("Admin access is not configured on the server.");
      } else {
        setError(err?.response?.data?.detail || "Could not load submissions.");
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token, division);
    // `load` is a stable wrapper over axios — re-creating it on every render
    // would cause an infinite refetch loop. Intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, division]);

  const switchDivision = (next) => {
    if (next === division) return;
    try {
      window.sessionStorage.setItem(DIVISION_KEY, next);
    } catch (err) {
      // sessionStorage blocked (private mode, embedded contexts). Selection
      // still works for this tab; it just won't persist across reloads.
      // eslint-disable-next-line no-console
      console.debug("AdminIntakesPage: sessionStorage write blocked", err);
    }
    setDivision(next);
    setFilter("all");
    setItems([]);
  };

  const stats = useMemo(() => {
    const total = items.length;
    const paid = items.filter((i) => i.status === "paid" || i.status === "in_build").length;
    const inBuild = items.filter((i) => i.status === "in_build").length;
    const awaitingKickoff = items.filter((i) => i.status === "paid").length;
    const inGhl = items.filter((i) => i.ghl_contact_id).length;
    const revenueCents = items
      .filter((i) => i.status === "paid" || i.status === "in_build")
      .reduce((acc, i) => acc + (i.stripe_amount_total || 0), 0);
    // Last 7 days (rolling) — paid count + revenue, based on paid_at timestamp.
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7 = items.filter(
      (i) =>
        (i.status === "paid" || i.status === "in_build") &&
        i.paid_at &&
        new Date(i.paid_at).getTime() >= sevenDaysAgo,
    );
    const last7RevenueCents = last7.reduce(
      (acc, i) => acc + (i.stripe_amount_total || 0),
      0,
    );
    return {
      total,
      paid,
      unpaid: total - paid,
      inBuild,
      awaitingKickoff,
      inGhl,
      revenueCents,
      last7Count: last7.length,
      last7RevenueCents,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "paid")
      return items.filter((i) => i.status === "paid" || i.status === "in_build");
    if (filter === "unpaid")
      return items.filter((i) => i.status !== "paid" && i.status !== "in_build");
    if (filter === "awaiting_kickoff")
      return items.filter((i) => i.status === "paid");
    if (filter === "in_build")
      return items.filter((i) => i.status === "in_build");
    return items;
  }, [items, filter]);

  const kickoff = async (briefId) => {
    try {
      const res = await axios.post(
        `${API}/admin/website-intake-submissions/${briefId}/kickoff`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Optimistic merge — replace the row in-place so the badge flips instantly
      setItems((prev) =>
        prev.map((it) => (it.id === briefId ? { ...it, ...res.data } : it)),
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not mark as kicked off.");
    }
  };

  if (!token) {
    return (
      <AdminTokenGate
        tokenInput={tokenInput}
        setTokenInput={setTokenInput}
        error={error}
        onUnlock={(t) => {
          window.sessionStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  const filterPills = [
    { id: "all", label: `All (${stats.total})`, websitesOnly: false },
    { id: "paid", label: `Paid (${stats.paid})`, websitesOnly: false },
    { id: "awaiting_kickoff", label: `Awaiting kickoff (${stats.awaitingKickoff})`, websitesOnly: true },
    { id: "in_build", label: `In build (${stats.inBuild})`, websitesOnly: true },
    { id: "unpaid", label: `Awaiting payment (${stats.unpaid})`, websitesOnly: false },
  ].filter((p) => !p.websitesOnly || cfg.supportsKickoff);

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="admin-intakes-page">
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay flex items-center gap-2">
                <ShieldCheck size={12} /> {cfg.eyebrow}
              </p>
              <h1 className="font-display mt-3 text-[2rem] sm:text-[2.4rem] md:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                {cfg.headline}
              </h1>
              <p className="mt-3 text-[14px] text-ink-muted leading-relaxed">
                {cfg.subhead}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/admin/deliveries"
                data-testid="admin-deliveries-link"
                className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-clay/10 px-4 py-2.5 text-[12.5px] text-clay hover:bg-clay hover:text-sand-100 transition-colors min-h-[40px]"
              >
                <Workflow size={13} /> Deliveries
              </Link>
              <Link
                to="/admin/payments"
                data-testid="admin-payments-link"
                className="inline-flex items-center gap-2 rounded-full border border-olive/40 bg-olive/10 px-4 py-2.5 text-[12.5px] text-olive hover:bg-olive hover:text-sand-100 transition-colors min-h-[40px]"
              >
                <CreditCard size={13} /> Payments
              </Link>
              <button
                onClick={() => load(token, division)}
                disabled={loading}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink hover:border-clay/50 transition-colors min-h-[40px]"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button
                onClick={() => {
                  window.sessionStorage.removeItem(TOKEN_KEY);
                  setToken("");
                  setItems([]);
                }}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink-muted hover:text-ink transition-colors min-h-[40px]"
              >
                Lock
              </button>
            </div>
          </div>

          {/* Division toggle — Websites ↔ Revenue Systems */}
          <div className="mt-6 inline-flex items-center rounded-full border border-sand-300 bg-sand-50 p-1">
            {Object.entries(DIVISIONS).map(([key, d]) => {
              const active = key === division;
              const Icon = d.Icon;
              return (
                <button
                  key={key}
                  onClick={() => switchDivision(key)}
                  data-testid={`admin-division-${key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] transition-colors min-h-[36px] ${
                    active ? "bg-ink-deep text-sand-100" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  <Icon size={12} /> {d.label}
                </button>
              );
            })}
          </div>

          {/* Stats — top row + Last 7 days bar */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <Stat label="Total briefs" value={stats.total} />
            <Stat label="Paid" value={stats.paid} accent="olive" />
            <Stat label="Awaiting" value={stats.unpaid} accent="clay" />
            <Stat label="Revenue (paid)" value={fmtMoney(stats.revenueCents, "USD")} />
          </div>
          <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Stat
              label="Last 7 days · paid"
              value={`${stats.last7Count}  ·  ${fmtMoney(stats.last7RevenueCents, "USD")}`}
              accent="olive"
              testid="admin-stat-last-7-days"
            />
            {cfg.supportsKickoff ? (
              <Stat
                label="Awaiting kickoff"
                value={stats.awaitingKickoff}
                accent="clay"
                testid="admin-stat-awaiting-kickoff"
              />
            ) : (
              <Stat
                label="In CRM (GHL)"
                value={stats.inGhl}
                accent="clay"
                testid="admin-stat-in-ghl"
              />
            )}
          </div>

          {/* Filter pills */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {filterPills.map((p) => (
              <button
                key={p.id}
                onClick={() => setFilter(p.id)}
                data-testid={`admin-filter-${p.id}`}
                className={`rounded-full px-4 py-2 text-[12.5px] transition-colors min-h-[40px] ${
                  filter === p.id
                    ? "bg-ink-deep text-sand-100 border border-ink-deep"
                    : "bg-sand-50 text-ink border border-sand-300 hover:border-clay/50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {error && <p className="mt-6 text-[13px] text-clay">{error}</p>}

          {/* Submission rows — compact mobile, balanced desktop */}
          <div className="mt-6 rounded-3xl border border-sand-300 bg-sand-50 overflow-hidden">
            {filtered.length === 0 && !loading && (
              <p className="p-10 text-center text-ink-muted text-[14px]">
                No submissions match this filter yet.
              </p>
            )}
            {filtered.map((it) => (
              <IntakeRow
                key={it.id}
                it={it}
                cfg={cfg}
                division={division}
                onKickoff={kickoff}
              />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <IosInstallPrompt />
    </div>
  );
}
